import React, { FormEvent, useEffect, useState, useCallback } from "react";
import { Form } from "react-bootstrap";
import { DriveItem } from "../../../../../interfaces/caseFileDrive/DriveFiles";
import {
  renameFile,
  renameFolder,
  checkDuplicateFolderNameAsync,
} from "../../../../../services/fileServices/UserDriverService";
import { toast } from "react-toastify";
import { useAuth } from "../../../../../firebase/AuthProvider";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import "../../../taskPage/RejectTaskModal.css";

interface RenameFileFolderProps {
  showRenameModal: boolean;
  handleCloseRenamerModal: () => void;
  renameTarget: DriveItem;
  authToken: string;
  refreshCurrentFolder: () => void;
  parentFolderId?: number;
}

function debounce<T extends (...args: any[]) => void>(fn: T, delay: number) {
  let timer: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

const RenameFileFolder = ({
  renameTarget,
  handleCloseRenamerModal,
  showRenameModal,
  authToken,
  refreshCurrentFolder,
  parentFolderId,
}: RenameFileFolderProps) => {
  const { currentUser } = useAuth();
  const [newFileFolderName, setNewFileFolderName] = useState<string>("");
  const [validated, setValidated] = useState<boolean>(false);
  const [extention, setExtention] = useState<string>();
  const [isDuplicateName, setIsDuplicateName] = useState<boolean>(false);
  const [suggestedName, setSuggestedName] = useState<string>("");
  const [originalName, setOriginalName] = useState<string>("");

  useEffect(() => {
    if (renameTarget.isFolder) {
      setNewFileFolderName(renameTarget.name);
      setOriginalName(renameTarget.name);
    } else {
      const lastDotIndex = renameTarget.name.lastIndexOf(".");
      const nameWithoutExtension = renameTarget.name.substring(0, lastDotIndex);
      const extension = renameTarget.name.substring(lastDotIndex + 1);
      setNewFileFolderName(nameWithoutExtension);
      setOriginalName(nameWithoutExtension);
      setExtention(extension);
    }
  }, [renameTarget]);

  // Debounced duplicate check
  const debouncedCheck = useCallback(
    debounce(async (name: string) => {
      if (!name || !currentUser) return;
      // Compose full name for files
      const checkName = renameTarget.isFolder
        ? name
        : name + (extention ? "." + extention : "");
      const payload = {
        userId: currentUser?.uid, // If DriveItem has userId
        name: checkName,
        parentFolderID: parentFolderId || null,
        isFolder: renameTarget.isFolder,
      };
      try {
        const res = await checkDuplicateFolderNameAsync(authToken, payload);
        if (res.success) {
          if (res.data.isDuplicate) {
            setIsDuplicateName(true);
            setSuggestedName(res.data.suggestedName);
          } else {
            setIsDuplicateName(false);
            setSuggestedName("");
          }
        }
      } catch (err) {
        setIsDuplicateName(false);
        setSuggestedName("");
      }
    }, 500),
    [authToken, parentFolderId, renameTarget.isFolder, extention]
  );

  useEffect(() => {
    // Only check for duplicates if the name has changed from the original
    if (newFileFolderName && newFileFolderName !== originalName) {
      debouncedCheck(newFileFolderName);
    } else {
      setIsDuplicateName(false);
      setSuggestedName("");
    }
  }, [newFileFolderName, debouncedCheck, originalName]);

  // handle Tab press → apply suggestion
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Tab" && isDuplicateName && suggestedName) {
      e.preventDefault();
      if (renameTarget.isFolder) {
        setNewFileFolderName(suggestedName);
      } else {
        // Remove extension from suggestion if present
        const lastDot = suggestedName.lastIndexOf(".");
        if (lastDot > 0) {
          setNewFileFolderName(suggestedName.substring(0, lastDot));
          setExtention(suggestedName.substring(lastDot + 1));
        } else {
          setNewFileFolderName(suggestedName);
        }
      }
      setIsDuplicateName(false);
    }
  };

  const DoRename = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    // Cheking validation form
    if (form.checkValidity() === false) {
      setValidated(true);
      return;
    }
    if (!renameTarget) return;
    if (isDuplicateName) return; // Prevent submit if duplicate

    if (renameTarget.name == newFileFolderName) {
      toast.error("Please Enter Different Name");
      return;
    }

    // If it's a folder:
    if (renameTarget.isFolder) {
      try {
        // Call your renameFolder service (example stub below).
        var res = await renameFolder(renameTarget.id, newFileFolderName, authToken);
        toast.success("Folder Renamed Successfully!");
        // Then refresh
        refreshCurrentFolder();
      } catch (err) {
        console.error("Rename Folder error:", err);
      } finally {
        handleCloseRenamerModal();
      }
    } else {
      // If it’s a file => rename logic, e.g. renameFile(token, renameTarget.id, newName)
      try {
        const fileName = newFileFolderName + "." + extention;
        const response = await renameFile(renameTarget.id, fileName, authToken);
        if (response.success) {
          refreshCurrentFolder();
          toast.success(response.message);
        } else {
          toast.error(response.message);
        }
      } catch (err: any) {
        console.error(err.message);
      } finally {
        handleCloseRenamerModal();
      }
    }
  };

  if (!showRenameModal) return null;

  return (
    <div className="modal-activity-reject-overlay" onClick={(e) => {
      if (e.target === e.currentTarget) {
        handleCloseRenamerModal();
      }
    }}>
      <div className="modal-activity-reject-container max-w-[480px]">
        <div className="modal-activity-reject-header">
          <h2 className="modal-activity-reject-title">Rename</h2>
          <button
            className="modal-activity-reject-close"
            onClick={handleCloseRenamerModal}
            type="button"
          >
            <FontAwesomeIcon icon={faTimes} className="w-[1rem] h-[1rem]" />
          </button>
        </div>

        <form
          onSubmit={DoRename}
          className="modal-activity-reject-form"
          noValidate
        >
          <div className="modal-activity-reject-body">
            <div className="form-group">
              <label htmlFor="renameInput" className="form-label-reject">
                Edit Name<span className="required">*</span>
              </label>
              <input
                id="renameInput"
                type="text"
                className={`form-input ${isDuplicateName ? "error" : ""}`}
                value={newFileFolderName}
                onChange={(e) => setNewFileFolderName(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter new name"
                required
              />
              {isDuplicateName && (
                <span className="error-message">
                  This name already exists. Try "{suggestedName}". Press TAB to accept suggestion.
                </span>
              )}
              {validated && !newFileFolderName && (
                <span className="error-message">
                  Please provide a valid Name.
                </span>
              )}
            </div>
          </div>

          <div className="modal-activity-reject-footer">
            <button
              type="button"
              className="btn btn-secondary cancel-btn"
              onClick={handleCloseRenamerModal}
            >
              Close
            </button>
            <button
              type="submit"
              className="btn btn-primary submit-btn"
              disabled={isDuplicateName}
            >
              Rename
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RenameFileFolder;
